import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { Organization } from '../../models/organization';
import { PageMeta } from '../../models/page-meta';
import { OrganizationsService } from '../../services/organizations/organizations';

@Component({
  selector: 'app-organizations',
  standalone: false,
  templateUrl: './organizations.html',
  styleUrls: ['./organizations.css']
})
export class Organizations implements OnInit, AfterViewInit {
  organizations: Organization[] = [];

  loading = false;
  search = '';

  page = 1;
  size = 10;

  meta: PageMeta = {
    page: 1,
    size: 10,
    totalItems: 0,
    totalPages: 0
  };

  createModel = {
    name: '',
    contactEmail: '',
    contactPhone: ''
  };

  creating = false;

  selectedOrganization: Organization | null = null;

editModel = {
  name: '',
  contactEmail: '',
  contactPhone: ''
};

updating = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private organizationsService: OrganizationsService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadOrganizations();
  }

  ngAfterViewInit(): void {
    this.route.queryParamMap.subscribe(params => {
      if (params.get('create') !== 'true') {
        return;
      }

      setTimeout(() => {
        this.openCreateModal();
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { create: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      });
    });
  }

  openCreateModal(): void {
    const modalEl = document.getElementById('createOrganizationModal');

    if (!modalEl) {
      return;
    }

    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  loadOrganizations(): void {
    this.loading = true;

    this.organizationsService
      .getOrganizations(this.page, this.size, this.search)
      .pipe(finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: response => {
          if (!response.success || !Array.isArray(response.data)) {
            this.organizations = [];
            this.meta = this.createEmptyMeta();
            this.toastr.error(response.message || 'Unable to load organizations.');
            this.cdr.detectChanges();
            return;
          }

          this.organizations = response.data;

          if (response.meta) {
            this.meta = {
              page: Number(response.meta.page || this.page),
              size: Number(response.meta.size || this.size),
              totalItems: Number(response.meta.totalItems || response.data.length),
              totalPages: Number(response.meta.totalPages || 1)
            };
          } else {
            this.meta = {
              page: this.page,
              size: this.size,
              totalItems: response.data.length,
              totalPages: Math.max(1, Math.ceil(response.data.length / this.size))
            };
          }

          this.cdr.detectChanges();
        },
        error: error => {
          this.organizations = [];
          this.meta = this.createEmptyMeta();

          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to load organizations.',
            'Organizations'
          );
          this.cdr.detectChanges();
        }
      });
  }

  private createEmptyMeta(): PageMeta {
    return {
      page: this.page,
      size: this.size,
      totalItems: 0,
      totalPages: 0
    };
  }

  createOrganization(): void {
    if (!this.createModel.name.trim()) {
      this.toastr.warning('Organization name is required.', 'Validation');
      return;
    }

    this.creating = true;

    this.organizationsService.createOrganization({
      name: this.createModel.name.trim(),
      contactEmail: this.createModel.contactEmail?.trim() || null,
      contactPhone: this.createModel.contactPhone?.trim() || null
    }).subscribe({
      next: response => {
        this.creating = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to create organization.');
          return;
        }

        this.toastr.success('Organization created successfully.');

        this.createModel = {
          name: '',
          contactEmail: '',
          contactPhone: ''
        };

        const modalEl = document.getElementById('createOrganizationModal');
        const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
        modal?.hide();

        this.loadOrganizations();
      },
      error: error => {
        this.creating = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to create organization.',
          'Organizations'
        );
      }
    });
  }

  openEditModal(org: Organization): void {
  this.selectedOrganization = org;

  this.editModel = {
    name: org.name,
    contactEmail: org.contactEmail || '',
    contactPhone: org.contactPhone || ''
  };

  const modalEl = document.getElementById('editOrganizationModal');
  const modal = new (window as any).bootstrap.Modal(modalEl);
  modal.show();
}

updateOrganization(): void {
  if (!this.selectedOrganization) return;

  if (!this.editModel.name.trim()) {
    this.toastr.warning('Organization name is required.', 'Validation');
    return;
  }

  this.updating = true;

  this.organizationsService.updateOrganization(this.selectedOrganization.id, {
    name: this.editModel.name.trim(),
    contactEmail: this.editModel.contactEmail?.trim() || null,
    contactPhone: this.editModel.contactPhone?.trim() || null
  }).subscribe({
    next: response => {
      this.updating = false;

      if (!response.success) {
        this.toastr.error(response.message || 'Unable to update organization.');
        return;
      }

      this.toastr.success('Organization updated successfully.');

      const modalEl = document.getElementById('editOrganizationModal');
      const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
      modal?.hide();

      this.loadOrganizations();
    },
    error: error => {
      this.updating = false;

      this.toastr.error(
        error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to update organization.',
        'Organizations'
      );
    }
  });
}

changeStatus(org: Organization, action: 'activate' | 'suspend' | 'disable'): void {
  const confirmed = confirm(`Are you sure you want to ${action} ${org.name}?`);

  if (!confirmed) return;

  const request =
    action === 'activate'
      ? this.organizationsService.activateOrganization(org.id)
      : action === 'suspend'
        ? this.organizationsService.suspendOrganization(org.id)
        : this.organizationsService.disableOrganization(org.id);

  request.subscribe({
    next: response => {
      if (!response.success) {
        this.toastr.error(response.message || 'Unable to update organization status.');
        return;
      }

      this.toastr.success(`Organization ${action}d successfully.`);
      this.loadOrganizations();
    },
    error: error => {
      this.toastr.error(
        error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to update organization status.',
        'Organizations'
      );
    }
  });
}

  searchOrganizations(): void {
    this.page = 1;
    this.loadOrganizations();
  }

  clearSearch(): void {
    this.search = '';
    this.page = 1;
    this.loadOrganizations();
  }

  previousPage(): void {
    if (this.page <= 1) return;

    this.page--;
    this.loadOrganizations();
  }

  nextPage(): void {
    if (this.page >= this.meta.totalPages) return;

    this.page++;
    this.loadOrganizations();
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 1:
        return 'Active';
      case 2:
        return 'Suspended';
      case 3:
        return 'Disabled';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 1:
        return 'text-bg-success';
      case 2:
        return 'text-bg-warning';
      case 3:
        return 'text-bg-secondary';
      default:
        return 'text-bg-light';
    }
  }
}
